import { syncThemeColor } from './vzhlad';

/**
 * Svetlý / tmavý režim.
 *
 * Tri voľby: svetlý, tmavý a „Systém“, ktorý ide za nastavením zariadenia aj
 * počas behu (Mac sa večer prepne sám). Kedysi boli iba dve a „systém“ platil
 * len pri úplne prvom otvorení — hneď potom sa výsledok uložil ako pevná
 * voľba, takže appka ostala svetlá aj po západe slnka.
 *
 * Trieda `.dark` sa na <html> zapisuje pred prvým vykreslením (installTheme
 * v main.tsx). Kým to robil useEffect v ThemeProvideri, tmavá appka prvý
 * snímok blikla na bielo.
 */

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_KEY = 'whoworks:theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

export const isThemeMode = (value: unknown): value is ThemeMode =>
  value === 'light' || value === 'dark' || value === 'system';

/** Uložená voľba; nič (alebo nezmysel) znamená „Systém“. */
export const readThemeMode = (storage: StorageLike | null | undefined): ThemeMode => {
  try {
    const stored = storage?.getItem(THEME_KEY);
    return isThemeMode(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
};

export const resolveTheme = (mode: ThemeMode, prefersDark: boolean): ResolvedTheme =>
  mode === 'system' ? (prefersDark ? 'dark' : 'light') : mode;

let currentMode: ThemeMode = 'system';
let currentResolved: ResolvedTheme = 'light';
const listeners = new Set<() => void>();
let snapshot: { mode: ThemeMode; theme: ResolvedTheme } = { mode: currentMode, theme: currentResolved };

const prefersDark = (win: Window): boolean => {
  try {
    return Boolean(win.matchMedia?.(DARK_QUERY).matches);
  } catch {
    return false;
  }
};

const apply = (win: Window, mode: ThemeMode): void => {
  const root = win.document.documentElement;
  const resolved = resolveTheme(mode, prefersDark(win));
  root.classList.toggle('dark', resolved === 'dark');
  root.dataset.themeMode = mode;
  currentMode = mode;
  currentResolved = resolved;
  if (snapshot.mode !== mode || snapshot.theme !== resolved) {
    snapshot = { mode, theme: resolved };
  }
  syncThemeColor(win.document);
  listeners.forEach((listener) => listener());
};

export const subscribeTheme = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Stabilný objekt (useSyncExternalStore porovnáva referenciu). */
export const getThemeSnapshot = (): { mode: ThemeMode; theme: ResolvedTheme } => snapshot;

export const setThemeMode = (mode: ThemeMode, win: Window = window): void => {
  apply(win, mode);
  try {
    win.localStorage.setItem(THEME_KEY, mode);
  } catch {
    // Nedá sa uložiť — režim platí aspoň do zatvorenia.
  }
};

let systemListener: ((event: MediaQueryListEvent) => void) | null = null;

/** Raz pri štarte, pred prvým vykreslením. */
export const installTheme = (win: Window = window): ResolvedTheme => {
  let storage: StorageLike | null = null;
  try {
    storage = win.localStorage;
  } catch {
    storage = null;
  }
  apply(win, readThemeMode(storage));

  // Pri „Systém“ nasleduj zariadenie aj počas behu. Pri pevnej voľbe sa
  // zmena systému ignoruje — to je presne dôvod, prečo si ju človek zvolil.
  try {
    const list = win.matchMedia?.(DARK_QUERY);
    if (list && !systemListener) {
      systemListener = () => {
        if (currentMode === 'system') apply(win, 'system');
      };
      list.addEventListener?.('change', systemListener);
    }
  } catch {
    // Bez matchMedia ostane pri tom, čo sa rozhodlo pri štarte.
  }
  return currentResolved;
};
